import {
  atualizarCategoriaSchema,
  criarCategoriaSchema,
  frota,
  gerarSlug,
  NIVEIS_DE_BLINDAGEM,
  type NivelDeBlindagem,
  ROTULO_DO_CAMBIO,
  ROTULO_DO_NIVEL_DE_BLINDAGEM,
  TIPOS_DE_CAMBIO,
  type TipoDeCambio,
} from '@locaveri/shared'
import { LoaderCircle, ShieldCheck } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { cn } from '@/lib/utils'

import { type Categoria, useAtualizarCategoria, useCriarCategoria } from './api'
import { GaleriaDeCategoria } from './galeria-de-categoria'

/**
 * Cadastro e edição de categoria — o PRODUTO que o cliente aluga.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE FORMULÁRIO É GRANDE
 * ---------------------------------------------------------------------------
 * Regra de ouro do domínio: o cliente aluga CATEGORIA, nunca modelo. Então tudo
 * o que ele negocia — caução, franquia de km, valor do excedente, idade mínima,
 * tempo de CNH, a promessa de classe ("Compass, Renegade ou similar") — vive
 * aqui, e não no veículo. Quebrar este formulário em "básico" e "avançado"
 * esconderia justamente os campos que definem o preço da diária na Fase 3.
 *
 * ---------------------------------------------------------------------------
 * VAZIO NÃO É ZERO (vida útil e residual)
 * ---------------------------------------------------------------------------
 * `vidaUtilEmMeses` e `residualEmPontosBase` nulos significam "HERDA da política
 * de custo do cliente", nunca zero. Um residual de 0% é uma decisão de negócio
 * legítima ("no fim da vida útil o carro não vale nada") e precisa poder ser
 * dita; se vazio e zero fossem a mesma coisa, ela não poderia — e a depreciação
 * de toda a frota daquela classe sairia errada no fechamento do mês. Por isso o
 * campo vazio vira `null` explícito no corpo, e a tela diz isso por escrito.
 *
 * ---------------------------------------------------------------------------
 * SEM `zodResolver`, E O CORPO VAI COMO ENTRADA
 * ---------------------------------------------------------------------------
 * `criarCategoriaSchema` TRANSFORMA: `caucaoBase` e `valorDoKmExcedente` saem
 * como `bigint`, e `JSON.stringify(bigint)` lança `TypeError`. O formulário
 * guarda o que a pessoa digitou, o Zod valida essa entrada e o que viaja é a
 * ENTRADA validada — nunca `validado.data`. Ver `lib/formulario.ts`.
 */

const CAMPOS = [
  'nome',
  'slug',
  'descricao',
  'nivelDeBlindagem',
  'lugares',
  'portas',
  'malas',
  'cambio',
  'modelosRepresentativos',
  'galeria',
  'idadeMinimaDoCondutor',
  'tempoMinimoDeCnhEmAnos',
  'caucaoBase',
  'kmDiarioIncluso',
  'valorDoKmExcedente',
  'maximoDeParcelas',
  'valorMinimoDaParcela',
  'vidaUtilEmMeses',
  'residualEmPontosBase',
  'ordemDeExibicao',
  'ativo',
] as const

interface FormularioDeCategoria {
  nome: string
  slug: string
  descricao: string
  nivelDeBlindagem: NivelDeBlindagem
  cambio: TipoDeCambio
  lugares: string
  portas: string
  malas: string
  modelosRepresentativos: string
  /**
   * As CHAVES do storage devolvidas pelo upload assinado, na ordem de exibição
   * (a primeira é a capa). Categorias cadastradas antes do uploader ainda podem
   * carregar URLs cruas aqui — ver `GaleriaDeCategoria`.
   *
   * O único campo, junto de `ativo`, que não é `string`: ele não é digitado, é
   * construído por upload, e um `<Textarea>` de URLs coladas era exatamente o
   * que produzia foto quebrada em produção sem erro em lugar nenhum.
   */
  galeria: string[]
  idadeMinimaDoCondutor: string
  tempoMinimoDeCnhEmAnos: string
  caucaoBase: string
  kmDiarioIncluso: string
  valorDoKmExcedente: string
  /** Parcelamento da venda (Fase 5). Vazio mantém o padrão do banco (à vista). */
  maximoDeParcelas: string
  valorMinimoDaParcela: string
  vidaUtilEmMeses: string
  residualEmPontosBase: string
  ordemDeExibicao: string
  /**
   * O único campo que NÃO é string: `z.boolean()` não faz coerção, e `"false"`
   * chegaria no contrato como um booleano verdadeiro — a categoria voltaria à
   * vitrine sozinha.
   */
  ativo: boolean
}

/**
 * O que um campo de dinheiro manda para o contrato.
 *
 * `deCampoDeReaisParaWire` devolve `null` quando não consegue ler o texto, e
 * mandar `null` num campo obrigatório trocaria "valor monetário inválido" por
 * "campo obrigatório" — mensagem que aponta para o problema errado. Então o
 * texto cru segue adiante e quem reprova é o `moneySchema`, com a mensagem dele.
 */
function dinheiroParaOContrato(texto: string): string {
  return deCampoDeReaisParaWire(texto) ?? texto
}

/** Campo numérico opcional: vazio vira `null` (herda), nunca `0`. */
function inteiroOuHerdado(texto: string): string | null {
  const limpo = texto.trim()

  return limpo.length === 0 ? null : limpo
}

/**
 * `2000` pontos-base lido como `20,00%`.
 *
 * Quociente e resto em inteiros, e não `pontos / 100` formatado com duas casas:
 * a divisão devolve um float e o texto passaria a depender do arredondamento do
 * formatador. O `padStart` sobre o resto dá os dois dígitos exatos, sempre. O
 * que viaja para a API é o inteiro em pontos-base, nunca este texto — ele
 * existe só para a pessoa conferir o que digitou.
 *
 * Exportado porque a listagem mostra a mesma premissa na frota da categoria, e
 * duas conversões separadas é como uma delas fica para trás.
 */
export function formatarPontosBase(pontos: number): string {
  const inteiro = Math.trunc(pontos / 100)
  const centesimos = pontos % 100

  return `${String(inteiro)},${String(centesimos).padStart(2, '0')}%`
}

/**
 * O residual digitado lido como percentual, ou `null` enquanto não der para
 * ler. A dica do campo não pode chutar: exibir "equivale a 0,00%" com o campo
 * vazio afirmaria que o residual é zero — e zero é uma decisão de negócio
 * diferente de herdar a política.
 */
function percentualDigitado(texto: string): string | null {
  const limpo = texto.trim()

  if (!/^[0-9]+$/.test(limpo)) {
    return null
  }

  const pontos = Number(limpo)

  if (!Number.isSafeInteger(pontos) || pontos > frota.PONTOS_BASE_MAXIMOS) {
    return null
  }

  return formatarPontosBase(pontos)
}

function valoresIniciais(categoria: Categoria | undefined): FormularioDeCategoria {
  return {
    nome: categoria?.nome ?? '',
    slug: categoria?.slug ?? '',
    descricao: categoria?.descricao ?? '',
    // `III_A` é o piso do blindado civil no Brasil — abaixo disso o carro nem é
    // comercializado como blindado, então é o palpite honesto para uma classe
    // nova nesta locadora.
    nivelDeBlindagem: categoria?.nivelDeBlindagem ?? 'III_A',
    cambio: categoria?.cambio ?? 'AUTOMATICO',
    lugares: categoria === undefined ? '' : String(categoria.lugares),
    portas: categoria === undefined ? '' : String(categoria.portas),
    malas: categoria === undefined ? '' : String(categoria.malas),
    modelosRepresentativos: categoria?.modelosRepresentativos ?? '',
    galeria: [...(categoria?.galeria ?? [])],
    idadeMinimaDoCondutor:
      categoria === undefined
        ? String(frota.IDADE_MINIMA_LEGAL_PARA_DIRIGIR)
        : String(categoria.idadeMinimaDoCondutor),
    tempoMinimoDeCnhEmAnos:
      categoria === undefined ? '0' : String(categoria.tempoMinimoDeCnhEmAnos),
    caucaoBase: paraCampoDeReais(categoria?.caucaoBase),
    kmDiarioIncluso: categoria === undefined ? '' : String(categoria.kmDiarioIncluso),
    valorDoKmExcedente: paraCampoDeReais(categoria?.valorDoKmExcedente),
    maximoDeParcelas: categoria === undefined ? '' : String(categoria.maximoDeParcelas),
    valorMinimoDaParcela: paraCampoDeReais(categoria?.valorMinimoDaParcela),
    vidaUtilEmMeses:
      categoria?.vidaUtilEmMeses === null || categoria?.vidaUtilEmMeses === undefined
        ? ''
        : String(categoria.vidaUtilEmMeses),
    residualEmPontosBase:
      categoria?.residualEmPontosBase === null || categoria?.residualEmPontosBase === undefined
        ? ''
        : String(categoria.residualEmPontosBase),
    ordemDeExibicao: categoria === undefined ? '0' : String(categoria.ordemDeExibicao),
    ativo: categoria?.ativo ?? true,
  }
}

export interface DialogoDeCategoriaProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita. */
  readonly categoria?: Categoria | undefined
}

export function DialogoDeCategoria({
  aberto,
  aoFechar,
  categoria,
}: DialogoDeCategoriaProps): ReactElement {
  const editando = categoria !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarCategoria()
  const atualizar = useAtualizarCategoria()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeCategoria>({ defaultValues: valoresIniciais(undefined) })

  useEffect(() => {
    // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir
    // "editar" depois de "nova" mostraria os valores do formulário anterior.
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(categoria))
    }
  }, [aberto, categoria, reset])

  const galeria = watch('galeria')
  const nivel = watch('nivelDeBlindagem')
  const nome = watch('nome')
  const slug = watch('slug')
  const residual = watch('residualEmPontosBase')

  const ocupado = criar.isPending || atualizar.isPending

  const slugDerivado = gerarSlug(nome)
  const dicaDoSlug = editando
    ? 'É o endereço público já divulgado. Trocar quebra o link salvo no app do cliente.'
    : slug.trim().length > 0 || slugDerivado.length === 0
      ? 'Endereço público da categoria. Vazio, é derivado do nome.'
      : `Vazio, vira "${slugDerivado}".`

  const percentualDoResidual = percentualDigitado(residual)
  const dicaDoResidual =
    percentualDoResidual === null
      ? `Quanto o veículo ainda vale no fim da vida útil. ${String(frota.PONTOS_BASE_MAXIMOS)} = 100%. Vazio usa o padrão da empresa.`
      : `Equivale a ${percentualDoResidual} do valor de aquisição.`

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const descricaoLimpa = valores.descricao.trim()

    const entrada = {
      nome: valores.nome,
      // Na edição o slug vai sempre (o campo já vem preenchido com o atual, e
      // mandá-lo é no-op); na criação, vazio é ausente para a API derivá-lo.
      ...(valores.slug.trim().length > 0 ? { slug: valores.slug.trim() } : {}),
      // Vazia vira `null`, e não `''`: no contrato de `@locaveri/shared` ausente
      // é "não mexe" e `null` é "apague", então string vazia gravaria um texto
      // em branco onde a coluna deveria voltar a ser nula — e some com a
      // diferença entre "não tem descrição" e "tem uma descrição vazia" para
      // quem monta a vitrine.
      descricao: descricaoLimpa.length > 0 ? descricaoLimpa : null,
      nivelDeBlindagem: valores.nivelDeBlindagem,
      lugares: valores.lugares,
      portas: valores.portas,
      malas: valores.malas,
      cambio: valores.cambio,
      modelosRepresentativos: valores.modelosRepresentativos,
      galeria: valores.galeria,
      idadeMinimaDoCondutor: valores.idadeMinimaDoCondutor,
      tempoMinimoDeCnhEmAnos: valores.tempoMinimoDeCnhEmAnos,
      caucaoBase: dinheiroParaOContrato(valores.caucaoBase),
      kmDiarioIncluso: valores.kmDiarioIncluso,
      valorDoKmExcedente: dinheiroParaOContrato(valores.valorDoKmExcedente),
      // Parcelamento (Fase 5): vazio fica FORA do corpo — no contrato, ausente
      // é "mantém o padrão do banco" (1 parcela, piso zero), e `''` reprovaria
      // na coerção numérica com a mensagem errada.
      ...(valores.maximoDeParcelas.trim().length === 0
        ? {}
        : { maximoDeParcelas: valores.maximoDeParcelas.trim() }),
      ...(valores.valorMinimoDaParcela.trim().length === 0
        ? {}
        : { valorMinimoDaParcela: dinheiroParaOContrato(valores.valorMinimoDaParcela) }),
      vidaUtilEmMeses: inteiroOuHerdado(valores.vidaUtilEmMeses),
      residualEmPontosBase: inteiroOuHerdado(valores.residualEmPontosBase),
      ordemDeExibicao: valores.ordemDeExibicao,
      ativo: valores.ativo,
    }

    const schema = editando ? atualizarCategoriaSchema : criarCategoriaSchema
    const validado = schema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    if (categoria !== undefined) {
      atualizar.mutate(
        { id: categoria.id, dados: entrada },
        {
          onSuccess: () => {
            toast.success('Categoria atualizada')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    criar.mutate(entrada, {
      onSuccess: (criada) => {
        toast.success('Categoria criada', {
          description: `Cadastre os veículos de ${criada.nome} para começar a alocar.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar categoria' : 'Nova categoria'}</DialogTitle>
          <DialogDescription>
            É o que o cliente aluga. Caução, franquia de km e requisitos do condutor ficam aqui — a
            placa entra só na alocação.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-6"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-foreground">Identidade e promessa</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Nome" obrigatorio erro={errors.nome?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('nome', { required: 'informe o nome da categoria' })}
                    placeholder="SUV Blindado III-A"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Slug" erro={errors.slug?.message} dica={dicaDoSlug}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('slug')}
                    placeholder="suv-blindado-iii-a"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Modelos representativos"
                obrigatorio
                erro={errors.modelosRepresentativos?.message}
                dica="É a frase que o cliente lê antes de reservar. Prometa a CLASSE, não o carro: o modelo exato só é definido na alocação."
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('modelosRepresentativos', {
                      required: 'informe ao menos um modelo de referência',
                    })}
                    placeholder="Compass, Renegade ou similar"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Descrição"
                erro={errors.descricao?.message}
                className="sm:col-span-2"
                dica="Texto de apoio da vitrine. Opcional."
              >
                {(campo) => (
                  <Textarea
                    {...campo}
                    {...register('descricao')}
                    rows={3}
                    maxLength={2000}
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4" disabled={ocupado}>
            <legend className="text-sm font-medium text-foreground">Ficha da classe</legend>

            <div className="space-y-2">
              <p className="text-sm text-fg-secondary">Nível de blindagem</p>
              <div
                role="radiogroup"
                aria-label="Nível de blindagem"
                className="grid grid-cols-2 gap-2 sm:grid-cols-3"
              >
                {NIVEIS_DE_BLINDAGEM.map((opcao) => {
                  const selecionado = nivel === opcao

                  return (
                    <button
                      key={opcao}
                      type="button"
                      role="radio"
                      aria-checked={selecionado}
                      disabled={ocupado}
                      onClick={() => {
                        setValue('nivelDeBlindagem', opcao)
                      }}
                      className={cn(
                        'flex items-center justify-center gap-2 rounded-lg border px-3 py-2',
                        'text-sm transition-colors focus-visible:outline-none focus-visible:ring-2',
                        'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70',
                        selecionado
                          ? 'border-primary bg-accent font-medium text-accent-foreground'
                          : 'border-border bg-surface text-fg-secondary hover:bg-elevated',
                      )}
                    >
                      <ShieldCheck className="size-4" aria-hidden="true" />
                      {ROTULO_DO_NIVEL_DE_BLINDAGEM[opcao]}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-fg-muted">
                Proteção balística pela NIJ 0108. Nível III-A é o piso do blindado civil no Brasil.
              </p>

              {/* O grupo de rádio não é um `Campo`, então o erro precisa de um
                  lugar próprio: `nivelDeBlindagem` está em `CAMPOS`, e sem isto
                  uma reprovação do servidor neste campo não apareceria em
                  lugar nenhum da tela. */}
              {errors.nivelDeBlindagem?.message === undefined ? null : (
                <p className="text-xs text-danger-fg">{errors.nivelDeBlindagem.message}</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Câmbio" erro={errors.cambio?.message}>
                {(campo) => (
                  <Select {...campo} {...register('cambio')} disabled={ocupado}>
                    {TIPOS_DE_CAMBIO.map((item) => (
                      <option key={item} value={item}>
                        {ROTULO_DO_CAMBIO[item]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>

              <Campo rotulo="Lugares" obrigatorio erro={errors.lugares?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('lugares', { required: 'informe o número de lugares' })}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Portas" obrigatorio erro={errors.portas?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('portas', { required: 'informe o número de portas' })}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Malas"
                obrigatorio
                erro={errors.malas?.message}
                dica="Malas grandes que cabem no porta-malas."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('malas', { required: 'informe o número de malas' })}
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-foreground">Regras comerciais</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Caução base"
                obrigatorio
                erro={errors.caucaoBase?.message}
                dica="Em reais. Ex.: 5.000,00"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('caucaoBase', {
                      required: 'informe a caução base',
                      validate: (valor: string) =>
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 5.000,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Valor do km excedente"
                obrigatorio
                erro={errors.valorDoKmExcedente?.message}
                dica="Cobrado por km rodado acima da franquia diária."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('valorDoKmExcedente', {
                      required: 'informe o valor do km excedente',
                      validate: (valor: string) =>
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 1,50',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Km diário incluso"
                obrigatorio
                erro={errors.kmDiarioIncluso?.message}
                dica="Franquia por diária. Zero significa km livre negociado por fora."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('kmDiarioIncluso', { required: 'informe a franquia de km' })}
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Idade mínima do condutor"
                obrigatorio
                erro={errors.idadeMinimaDoCondutor?.message}
                dica={`Nunca abaixo de ${String(frota.IDADE_MINIMA_LEGAL_PARA_DIRIGIR)} anos.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('idadeMinimaDoCondutor', { required: 'informe a idade mínima' })}
                    type="number"
                    min={frota.IDADE_MINIMA_LEGAL_PARA_DIRIGIR}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Tempo mínimo de CNH"
                obrigatorio
                erro={errors.tempoMinimoDeCnhEmAnos?.message}
                dica="Em anos completos. Zero = sem exigência."
                className="sm:col-span-2"
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('tempoMinimoDeCnhEmAnos', {
                      required: 'informe o tempo mínimo de CNH',
                    })}
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Máximo de parcelas"
                erro={errors.maximoDeParcelas?.message}
                dica="Da venda das diárias no cartão. 1 = à vista. Vazio mantém o padrão (à vista)."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('maximoDeParcelas')}
                    type="number"
                    min={1}
                    max={12}
                    inputMode="numeric"
                    placeholder="à vista"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Valor mínimo da parcela"
                erro={errors.valorMinimoDaParcela?.message}
                dica="Nenhuma parcela fica abaixo disto — o plano reduz as parcelas até caber. Vazio = sem piso."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('valorMinimoDaParcela', {
                      validate: (valor: string) =>
                        valor.trim().length === 0 ||
                        deCampoDeReaisParaWire(valor) !== null ||
                        'valor inválido — use o formato 500,00',
                    })}
                    inputMode="decimal"
                    placeholder="0,00"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-foreground">
              Premissas de depreciação
            </legend>

            <Alert variant="info">
              <AlertTitle>Vazio não é zero</AlertTitle>
              <AlertDescription>
                Deixe os dois campos em branco para usar o padrão da empresa — é o normal. O padrão
                fica em <strong>Custos da frota</strong>, no botão{' '}
                <strong>Política de custo</strong>. Preencher aqui vale só para esta categoria. Zero
                é uma decisão explícita (&ldquo;no fim da vida útil não vale nada&rdquo;) e sai
                diferente no fechamento do mês.
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Vida útil"
                erro={errors.vidaUtilEmMeses?.message}
                dica="Em meses. Vazio usa o padrão da empresa."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('vidaUtilEmMeses')}
                    type="number"
                    min={1}
                    inputMode="numeric"
                    placeholder="padrão da empresa"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Residual em pontos-base"
                erro={errors.residualEmPontosBase?.message}
                dica={dicaDoResidual}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('residualEmPontosBase')}
                    type="number"
                    min={0}
                    max={frota.PONTOS_BASE_MAXIMOS}
                    inputMode="numeric"
                    placeholder="padrão da empresa"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="text-sm font-medium text-foreground">Vitrine</legend>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Ordem de exibição"
                erro={errors.ordemDeExibicao?.message}
                dica="Menor aparece primeiro no app e no catálogo."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    // `required` porque o contrato coage o valor: campo vazio
                    // chega ao `z.coerce.number()` como `''` e vira `0` calado,
                    // e a categoria saltaria para a frente da vitrine sem
                    // ninguém ter pedido. Vazio aqui é engano, não "herda".
                    {...register('ordemDeExibicao', { required: 'informe a ordem de exibição' })}
                    type="number"
                    min={0}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>

            {/* Fora da grade de duas colunas: a galeria é uma grade dentro de
                outra, e espremê-la numa coluna deixaria a miniatura pequena
                demais para conferir se a placa aparece na foto. */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Galeria</p>
              <GaleriaDeCategoria
                valor={galeria}
                aoMudar={(proximo) => {
                  // `shouldDirty` para o formulário saber que houve mudança;
                  // sem ele, subir uma foto e salvar sem tocar em mais nada
                  // pareceria (para qualquer guarda de "descartar alterações?")
                  // um formulário intocado.
                  setValue('galeria', proximo, { shouldDirty: true })
                }}
                desabilitado={ocupado}
              />
              {errors.galeria?.message === undefined ? null : (
                <p className="text-xs text-danger-fg">{errors.galeria.message}</p>
              )}
            </div>

            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
                bg-surface p-3 text-sm"
            >
              <Checkbox {...register('ativo')} className="mt-0.5" disabled={ocupado} />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">No catálogo</span>
                <span className="block text-xs text-fg-secondary">
                  Desmarcado, a categoria sai da vitrine e do app sem mexer na frota. É o caminho
                  para tirar uma classe de circulação quando ela ainda tem veículos vinculados.
                </span>
              </span>
            </label>

            {errors.ativo?.message === undefined ? null : (
              <p className="text-xs text-danger-fg">{errors.ativo.message}</p>
            )}
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar categoria'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
